import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getVideos, getPendingPaidAmount, paidAllReviewed, updateReviewedStatus, updatePaidStatus, hideVideo, showVideo } from '../services/api';
import VideoTable from '../components/VideoTable';
import PaginationControls from '../components/PaginationControls';
import LoadingSpinner from '../components/LoadingSpinner';
import CreateVideoModal from '../components/CreateVideoModal';
import { useAuth } from '../context/AuthContext';
import { useT } from '../utils/i18n';
import { deriveStatus } from '../utils/videoStatus';

// Cap on concurrent requests fired by a bulk action so selecting 100+ rows does
// not open 100 simultaneous connections to the backend.
const BULK_CONCURRENCY = 10;

const DEFAULT_ITEMS_PER_PAGE = 50;
const ITEMS_PER_PAGE_OPTIONS = [20, 50, 100, 200, 500, 1000];

// Labels are translated at render time, not here — a module-level t() would
// freeze whichever language was active when the module first loaded.
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'pending_paid', label: 'Pending Paid' },
  { value: 'paid', label: 'Paid' },
];

const NARRATOR_OPTIONS = [
  { value: 'Won', label: 'ว่าน' },
  { value: 'Waan', label: 'หวาน' },
];

const VideoListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const t = useT();

  // Read from URL or use defaults (default status is 'new')
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const sortKey = searchParams.get('sort_by') || 'id';
  const sortOrder = searchParams.get('order') || 'asc';
  const statusFilter = searchParams.get('status') ?? 'new'; // Default to 'new'
  const searchQuery = searchParams.get('search') || '';
  const narrator = searchParams.get('narrator') || 'Won'; // Default to 'Won'
  const showHidden = searchParams.get('show_hidden') === 'true'; // Read from URL
  const itemsPerPage = parseInt(searchParams.get('per_page')) || DEFAULT_ITEMS_PER_PAGE;

  const [videos, setVideos] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [pending_paid_amount, setPendingPaidAmount] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessingPaidAll, setIsProcessingPaidAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const [bulkActionNarrator, setBulkActionNarrator] = useState('Won');
  const [bulkActionType, setBulkActionType] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Helper to update URL params
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const fetchVideos = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    // Clear selections when fetching new page
    setSelectedVideoIds(new Set());
    try {
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sort_by: sortKey,
        order: sortOrder,
        narrator: narrator,
        show_hidden: showHidden,
      };
      // Only add search param if provided
      if (searchQuery) {
        params.search = searchQuery;
      }
      // Only add status param if not 'all' (all = no filter)
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      // These two are independent — fetch them in parallel so the list load
      // pays one round-trip latency, not two back-to-back.
      const [response, pendingPaidResponse] = await Promise.all([
        getVideos(params),
        getPendingPaidAmount(),
      ]);
      setVideos(response.data.videos);
      setTotalVideos(response.data.total);
      setPendingPaidAmount(pendingPaidResponse.data);
    } catch (err) {
      console.error("Failed to fetch videos:", err);
      setError(`${t('Failed to load videos')}: ${err.response?.data?.detail || err.message}`);
      setVideos([]); // Clear videos on error
      setTotalVideos(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortKey, sortOrder, statusFilter, searchQuery, narrator, showHidden, itemsPerPage, t]);

  // Keep local input in sync when the URL search param changes
  useEffect(() => {
    setSearchTerm(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]); // fetchVideos is memoized by useCallback

  const handlePaidAll = async () => {
    // Use window.confirm to avoid ESLint "no-restricted-globals" rule
    const narratorLabel = narrator === 'Won' ? 'ว่าน' : 'หวาน';
    const confirmed = window.confirm(`Mark all reviewed (${narratorLabel}) audio descriptions as paid?`);
    if (!confirmed) return;
    setIsProcessingPaidAll(true);
    try {
      await paidAllReviewed(narrator);
      // Refresh listing and pending amounts
      await fetchVideos();
      alert(`Marked all reviewed (${narratorLabel}) items as paid`);
    } catch (err) {
      console.error('Failed to mark paid all:', err);
      alert('Failed to mark items as paid: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsProcessingPaidAll(false);
    }
  }

  const handlePageChange = (pageNumber) => {
    updateSearchParams({ page: pageNumber.toString() });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    updateSearchParams({ per_page: newItemsPerPage.toString(), page: '1' });
  };

  // Stable so React.memo on VideoTable/VideoRow holds — passing a fresh closure
  // each render would defeat memoization and re-render every row on any change.
  const handleVideoToggleSelection = useCallback((videoId) => {
    setSelectedVideoIds(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(videoId)) {
        newSelected.delete(videoId);
      } else {
        newSelected.add(videoId);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAllToggle = useCallback(() => {
    setSelectedVideoIds(prev => {
      if (prev.size === videos.length && videos.length > 0) {
        return new Set(); // Unselect all
      }
      return new Set(videos.map(v => v.id)); // Select all on current page
    });
  }, [videos]);

  const handleSort = useCallback((key) => {
    let direction = 'asc';
    // If sorting by the same key, toggle direction
    if (sortKey === key && sortOrder === 'asc') {
      direction = 'desc';
    }
    updateSearchParams({ sort_by: key, order: direction, page: '1' });
  }, [sortKey, sortOrder, updateSearchParams]);

  // --- Optimistic local updates -------------------------------------------
  // Row actions patch just the affected video in local state instead of
  // re-fetching the whole page. This keeps the current selection intact and
  // makes marking reviewed/paid/hidden feel instant.

  const applyVideoPatch = useCallback((videoId, patch) => {
    setVideos(prev => prev.map(v => {
      if (v.id !== videoId) return v;
      const next = { ...v, ...patch };
      if (patch.audio_description) {
        next.audio_description = v.audio_description
          ? { ...v.audio_description, ...patch.audio_description }
          : v.audio_description;
      }
      // Recompute the derived status so the row's status icon updates too.
      next.status = deriveStatus(next, narrator);
      return next;
    }));
  }, [narrator]);

  const removeVideoLocally = useCallback((videoId) => {
    setVideos(prev => prev.filter(v => v.id !== videoId));
    setTotalVideos(prev => Math.max(0, prev - 1));
  }, []);

  // Single entry point for the three row buttons. Applies an optimistic patch,
  // fires the API call, and rolls back (or resyncs) on failure so the UI never
  // shows a state the server rejected.
  const handleRowAction = useCallback(async (video, actionType) => {
    const ad = video.audio_description;

    if (actionType === 'reviewed' || actionType === 'paid') {
      if (!ad) return;
      const field = actionType === 'reviewed'
        ? (narrator === 'Waan' ? 'is_reviewed_waan' : 'is_reviewed')
        : (narrator === 'Waan' ? 'is_paid_waan' : 'is_paid');
      const nextVal = !ad[field];
      applyVideoPatch(video.id, { audio_description: { [field]: nextVal } });
      try {
        if (actionType === 'reviewed') {
          await updateReviewedStatus(ad.id, narrator, nextVal);
        } else {
          await updatePaidStatus(ad.id, narrator, nextVal);
        }
      } catch (err) {
        console.error(`Failed to toggle ${actionType} status`, err);
        applyVideoPatch(video.id, { audio_description: { [field]: !nextVal } }); // rollback
        alert(`Failed to update ${actionType} status.`);
      }
      return;
    }

    if (actionType === 'hidden') {
      const nextHidden = !video.is_hidden;
      const willVanish = nextHidden && !showHidden;
      if (willVanish) {
        removeVideoLocally(video.id);
      } else {
        applyVideoPatch(video.id, { is_hidden: nextHidden });
      }
      try {
        await (nextHidden ? hideVideo(video.id) : showVideo(video.id));
      } catch (err) {
        console.error('Failed to toggle hidden status', err);
        if (willVanish) {
          await fetchVideos(); // rare error path: resync since position was lost
        } else {
          applyVideoPatch(video.id, { is_hidden: !nextHidden }); // rollback
        }
        alert('Failed to update hidden status.');
      }
    }
  }, [narrator, showHidden, applyVideoPatch, removeVideoLocally, fetchVideos]);

  const handleStatusChange = (newStatus) => {
    updateSearchParams({ status: newStatus, page: '1' });
  };

  const handleNarratorChange = (newNarrator) => {
    updateSearchParams({ narrator: newNarrator, page: '1' });
  };

  const handleShowHiddenChange = (e) => {
    updateSearchParams({ show_hidden: e.target.checked ? 'true' : '', page: '1' });
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    const value = (searchTerm || '').trim();
    updateSearchParams({ search: value, page: '1' });
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    updateSearchParams({ search: '', page: '1' });
  };

  const handleBulkAction = async () => {
    if (selectedVideoIds.size === 0 || !bulkActionType) {
      alert('Please select videos and an action');
      return;
    }

    setBulkActionInProgress(true);
    try {
      const selectedIds = Array.from(selectedVideoIds);
      let successCount = 0;
      let failCount = 0;

      // The action for one selected video (null = nothing to do).
      const runOne = (videoId) => {
        const video = videos.find(v => v.id === videoId);
        if (!video) return null;
        switch (bulkActionType) {
          case 'review':
            return video.audio_description ? updateReviewedStatus(video.audio_description.id, bulkActionNarrator, true) : null;
          case 'not_review':
            return video.audio_description ? updateReviewedStatus(video.audio_description.id, bulkActionNarrator, false) : null;
          case 'paid':
            return video.audio_description ? updatePaidStatus(video.audio_description.id, bulkActionNarrator, true) : null;
          case 'not_paid':
            return video.audio_description ? updatePaidStatus(video.audio_description.id, bulkActionNarrator, false) : null;
          case 'hide':
            return hideVideo(videoId);
          case 'show':
            return showVideo(videoId);
          default:
            return null;
        }
      };

      // Run in bounded-concurrency batches so we don't stampede the backend with
      // one connection per selected row, but still overlap requests instead of
      // the old fully-sequential await-per-row.
      for (let i = 0; i < selectedIds.length; i += BULK_CONCURRENCY) {
        const batch = selectedIds.slice(i, i + BULK_CONCURRENCY);
        const results = await Promise.allSettled(
          batch.map((videoId) => {
            const p = runOne(videoId);
            return p == null ? Promise.resolve('skip') : p;
          })
        );
        for (const [idx, r] of results.entries()) {
          if (r.status === 'fulfilled') {
            if (r.value !== 'skip') successCount++;
          } else {
            console.error(`Failed to process video ${batch[idx]}:`, r.reason);
            failCount++;
          }
        }
      }

      alert(`Bulk action completed: ${successCount} succeeded${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setSelectedVideoIds(new Set());
      setBulkActionType('');
      await fetchVideos();
    } catch (err) {
      console.error('Bulk action error:', err);
      alert('Error performing bulk action: ' + (err.message || 'Unknown error'));
    } finally {
      setBulkActionInProgress(false);
    }
  };

  // Memoized so a fresh object each render doesn't defeat VideoTable's React.memo
  // (e.g. every keystroke in the search box would otherwise re-render all rows).
  const sortConfig = useMemo(() => ({ key: sortKey, direction: sortOrder }), [sortKey, sortOrder]);

  return (
    <div className="video-list-page">
      {isAdmin && (
      <div className="pending-paid-amount">
        {/* format pending to to two decimal places */}
      <p>เงินเหลือจ่ายว่าน:   {pending_paid_amount.pending_amount} บาท</p>
      <p>เงินเหลือจ่ายหวาน:   {pending_paid_amount.pending_amount_waan} บาท</p>
      <p>เงินว่านที่ยังไม่ได้ทำ:   {pending_paid_amount.pending_new_amount} บาท</p>
      <p>เงินหวานที่ยังไม่ได้ทำ:   {pending_paid_amount.pending_new_amount_waan} บาท</p>
      <div style={{ marginTop: '0.5rem' }}>
        <button className="btn btn-primary" onClick={handlePaidAll} disabled={isProcessingPaidAll}>
          {isProcessingPaidAll ? 'Processing...' : `Paid all (${narrator === 'Won' ? 'ว่าน' : 'หวาน'})`}
        </button>
        <button className="btn btn-success ms-2" onClick={() => setShowCreateModal(true)}>
          + Create Video
        </button>
      </div>
      </div>
      )}
      <h2>{t('Video List')}</h2>
      
      {/* Status Filter */}
      <div className="status-filter" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        {/* Search box (searches by title only) */}
        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder={t('Search by title...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(e); }}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <button type="submit" className="btn btn-secondary">{t('Search')}</button>
          <button type="button" className="btn btn-light" onClick={handleSearchClear}>{t('Clear')}</button>
        </form>

        <div style={{ marginLeft: 'auto' }}>
        <label htmlFor="narrator-select" style={{ marginRight: '0.5rem' }}>{t('Narrator:')}</label>
        <select
          id="narrator-select"
          value={narrator}
          onChange={(e) => handleNarratorChange(e.target.value)}
          style={{ padding: '0.5rem', marginRight: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          {NARRATOR_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label htmlFor="status-select" style={{ marginRight: '0.5rem' }}>{t('Filter by Status:')}</label>
        <select
          id="status-select"
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          style={{ padding: '0.5rem', marginRight: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
        <label htmlFor="show-hidden-checkbox" style={{ marginRight: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            id="show-hidden-checkbox"
            type="checkbox"
            checked={showHidden}
            onChange={handleShowHiddenChange}
            style={{ margin: 0 }}
          />
          {t('Show Hidden Videos')}
        </label>
        <label htmlFor="items-per-page-select" style={{ marginLeft: '2rem', marginRight: '0.5rem' }}>{t('Videos per page:')}</label>
        <select
          id="items-per-page-select"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
          style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          {ITEMS_PER_PAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      
      {/* Bulk Actions Toolbar — admin only. */}
      {isAdmin && selectedVideoIds.size > 0 && (
        <div className="bulk-actions-toolbar" style={{
          marginBottom: '1rem', 
          padding: '1rem', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px', 
          border: '1px solid #ddd',
          display: 'flex',
          gap: '1rem',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: 'bold' }}>
            {selectedVideoIds.size} video{selectedVideoIds.size !== 1 ? 's' : ''} selected
          </span>
          
          <label htmlFor="bulk-narrator-select" style={{ marginRight: '0.5rem' }}>Narrator:</label>
          <select
            id="bulk-narrator-select"
            value={bulkActionNarrator}
            onChange={(e) => setBulkActionNarrator(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="Won">ว่าน</option>
            <option value="Waan">หวาน</option>
          </select>
          
          <label htmlFor="bulk-action-select" style={{ marginRight: '0.5rem' }}>Action:</label>
          <select
            id="bulk-action-select"
            value={bulkActionType}
            onChange={(e) => setBulkActionType(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">-- Select Action --</option>
            <option value="review">Review</option>
            <option value="not_review">Not Review</option>
            <option value="paid">Paid</option>
            <option value="not_paid">Not Paid</option>
            <option value="show">Show</option>
            <option value="hide">Hide</option>
          </select>
          
          <button 
            className="btn btn-primary"
            onClick={handleBulkAction}
            disabled={bulkActionInProgress || !bulkActionType}
            style={{ marginLeft: 'auto' }}
          >
            {bulkActionInProgress ? 'Processing...' : 'Execute'}
          </button>
        </div>
      )}
      
      {error && <div className="alert alert-danger">{error}</div>}
      {isLoading ? (
        <div className="text-center">
          <LoadingSpinner />
          <p>{t('Loading videos...')}</p>
        </div>
      ) : (
        <>
          <VideoTable
            videos={videos}
            sortConfig={sortConfig}
            requestSort={handleSort}
            narrator={narrator}
            selectedVideoIds={selectedVideoIds}
            onVideoToggleSelection={handleVideoToggleSelection}
            onSelectAllToggle={handleSelectAllToggle}
            onRowAction={handleRowAction}
          />
          <PaginationControls
            currentPage={currentPage}
            totalItems={totalVideos}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </>
      )}
      <CreateVideoModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreateSuccess={fetchVideos}
      />
    </div>
  );
};

export default VideoListPage;